import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, Pencil, Trash2, Home, Users, Server, DoorOpen, Package, Zap, ChevronDown, ChevronUp, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { getRooms, createRoom, updateRoom, deleteRoom, getEspDevices } from '@/lib/api';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const ICONS = [
  { key: 'building', Icon: Building2, label: 'Bina' },
  { key: 'home', Icon: Home, label: 'Ev' },
  { key: 'meeting', Icon: Users, label: 'Toplantı' },
  { key: 'server', Icon: Server, label: 'Sunucu' },
  { key: 'door', Icon: DoorOpen, label: 'Koridor' },
  { key: 'storage', Icon: Package, label: 'Depo' },
];

const ICON_MAP = Object.fromEntries(ICONS.map(i => [i.key, i.Icon]));

const IconPicker = ({ value, onChange }) => (
  <div className="grid grid-cols-6 gap-2">
    {ICONS.map(({ key, Icon, label }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all ${
          value === key ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'
        }`}
        data-testid={`icon-pick-${key}`}
        title={label}
      >
        <Icon size={18} />
        <span className="text-[9px] text-muted-foreground">{label}</span>
      </button>
    ))}
  </div>
);

// Relay Assignment Picker per ESP device
const RelayAssignmentPicker = ({ assignments, onChange, espDevices }) => {
  const [expanded, setExpanded] = useState(true);

  const getAssigned = (espId) =>
    assignments.find(a => a.esp_device_id === espId)?.relay_numbers || [];

  const toggleRelay = (espId, num) => {
    const current = getAssigned(espId);
    const updated = current.includes(num)
      ? current.filter(n => n !== num)
      : [...current, num].sort((a, b) => a - b);

    const newAssignments = assignments.filter(a => a.esp_device_id !== espId);
    if (updated.length > 0) newAssignments.push({ esp_device_id: espId, relay_numbers: updated });
    onChange(newAssignments);
  };

  const totalSelected = assignments.reduce((s, a) => s + a.relay_numbers.length, 0);

  if (espDevices.length === 0) return null;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-2 w-full text-sm font-medium hover:text-primary transition-colors"
      >
        <Zap size={14} className="text-primary" />
        <span>Röle Atamaları</span>
        {totalSelected > 0 && <Badge variant="outline" className="text-xs ml-1">{totalSelected} röle seçili</Badge>}
        <span className="ml-auto text-muted-foreground">{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>

      {expanded && (
        <div className="space-y-3 bg-muted/30 rounded-xl p-3 border border-border">
          <p className="text-xs text-muted-foreground">
            Bu odada hangi ESP cihazının hangi röleleri kullanılacağını seçin
          </p>
          {espDevices.map(dev => {
            const assigned = getAssigned(dev.id);
            const nums = dev.relay_numbers?.length > 0 ? dev.relay_numbers : Array.from({length: 16}, (_, i) => i + 1);
            return (
              <div key={dev.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${dev.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                  <span className="text-xs font-semibold">{dev.name}</span>
                  {dev.zone && <Badge variant="outline" className="text-[10px]">{dev.zone}</Badge>}
                  {assigned.length > 0 && (
                    <span className="text-[10px] text-muted-foreground ml-auto">{assigned.length} seçili</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {nums.map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => toggleRelay(dev.id, n)}
                      className={`h-8 w-8 rounded-lg text-xs font-bold font-mono-tech transition-all ${
                        assigned.includes(n)
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted text-muted-foreground hover:bg-muted/70'
                      }`}
                      data-testid={`relay-assign-${dev.id}-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                {dev !== espDevices[espDevices.length - 1] && <Separator />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RoomFormDialog = ({ trigger, initial, espDevices, onSave }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [icon, setIcon] = useState('building');
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleOpen = (v) => {
    if (v) {
      setName(initial?.name || '');
      setDesc(initial?.description || '');
      setIcon(initial?.icon || 'building');
      setAssignments(initial?.relay_assignments?.map(a => ({
        esp_device_id: a.esp_device_id,
        relay_numbers: a.relay_numbers || []
      })) || []);
    }
    setOpen(v);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Oda adı giriniz'); return; }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: desc.trim(),
        icon,
        relay_assignments: assignments
      });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="room-form-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? 'Odayı Düzenle' : 'Yeni Oda Ekle'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Oda Adı *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Toplantı Odası 1" data-testid="room-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Açıklama</Label>
            <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Opsiyonel açıklama" data-testid="room-desc-input" />
          </div>
          <div className="space-y-1.5">
            <Label>İkon</Label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
          {espDevices.length > 0 && (
            <>
              <Separator />
              <RelayAssignmentPicker
                assignments={assignments}
                onChange={setAssignments}
                espDevices={espDevices}
              />
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-room-btn">
            {saving ? 'Kaydediliyor...' : initial ? 'Kaydet' : 'Ekle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [espDevices, setEspDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getRooms(), getEspDevices()])
      .then(([r, e]) => { setRooms(r.data); setEspDevices(e.data); })
      .catch(() => toast.error('Veriler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (data) => {
    try {
      const res = await createRoom(data);
      setRooms(prev => [...prev, res.data]);
      toast.success('Oda eklendi');
    } catch {
      toast.error('Oda eklenemedi');
      throw new Error();
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await updateRoom(id, data);
      setRooms(prev => prev.map(r => r.id === id ? res.data : r));
      toast.success('Oda güncellendi');
    } catch {
      toast.error('Oda güncellenemedi');
      throw new Error();
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRoom(id);
      setRooms(prev => prev.filter(r => r.id !== id));
      toast.success('Oda silindi');
    } catch {
      toast.error('Oda silinemedi');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Odalar</h1>
          <p className="text-muted-foreground text-sm mt-1">Oda yönetimi ve düzenleme</p>
        </div>
        <RoomFormDialog
          trigger={
            <Button className="gap-2" data-testid="add-room-btn">
              <Plus size={16} /> Oda Ekle
            </Button>
          }
          espDevices={espDevices}
          onSave={handleCreate}
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : rooms.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="empty-rooms-list">
          <Building2 size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground font-medium">Henüz oda eklenmemiş</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rooms-list">
          {rooms.map(room => {
            const RoomIcon = ICON_MAP[room.icon] || Building2;
            // Atanmış röle sayısı
            const totalRelays = (room.relay_assignments || []).reduce((s, a) => s + (a.relay_numbers?.length || 0), 0);
            return (
              <div
                key={room.id}
                className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:border-primary/30 transition-all animate-fade-in-up"
                data-testid={`room-item-${room.id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <RoomIcon size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{room.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {room.description && (
                      <p className="text-xs text-muted-foreground truncate">{room.description}</p>
                    )}
                    {totalRelays > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {(room.relay_assignments || []).map(a => {
                          const dev = espDevices.find(d => d.id === a.esp_device_id);
                          return a.relay_numbers.slice(0, 3).map(n => (
                            <Badge key={`${a.esp_device_id}-${n}`} variant="outline" className="text-[10px] gap-0.5 px-1.5 h-4 font-mono-tech">
                              <Zap size={8} />{dev?.name?.slice(0, 4) || '?'} R{n}
                            </Badge>
                          ));
                        })}
                        {totalRelays > 3 && <Badge variant="outline" className="text-[10px] px-1.5 h-4">+{totalRelays - 3}</Badge>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/rooms/${room.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Detaya Git" data-testid={`view-room-${room.id}`}>
                      <ArrowRight size={14} />
                    </Button>
                  </Link>
                  <RoomFormDialog
                    trigger={
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-room-${room.id}`}>
                        <Pencil size={14} />
                      </Button>
                    }
                    initial={room}
                    espDevices={espDevices}
                    onSave={(data) => handleUpdate(room.id, data)}
                  />
                  <ConfirmDeleteDialog
                    testId={`delete-room-${room.id}`}
                    title="Odayı Sil"
                    description={`"${room.name}" odasını ve tüm cihazlarını silmek istediğinizden emin misiniz?`}
                    onConfirm={() => handleDelete(room.id)}
                    trigger={
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-room-${room.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoomManagement;
